import React, { useState, useEffect, useRef } from "react";
import { useDispatch } from "react-redux";
import { setBuilderConfig } from "../../../store/slices/builder.slice";
import { Segmented, ColorPicker, Button, Tooltip, Tag, Drawer, List, Popconfirm } from "antd";
import { SaveOutlined, HistoryOutlined, DeleteOutlined } from "@ant-design/icons";
import { ClientBuilderPanel, DASHBOARD_ONLY_KEYS } from "../ClientBuilderPanel/ClientBuilderPanel";
import { HistoryRow } from "./HistoryRow";
import { useTranslation } from "react-i18next";
import { toast } from "react-toastify";
import { getConfig, getBrandDefaults } from "../../../config";
import { siteConfigService, ADMIN_KEY } from "../../../services/builder-services/builder.service";
import { applyAdminTheme } from "../../../helpers/applyBuilderTheme";
import { COMPONENT_REGISTRY, buildDefaultEntry, mergeEntry } from "../ClientBuilderPanel/componentRegistry";
import css from "./BuilderComponent.module.css";

const COLOR_GROUPS = [
    {
        label: "Brand",
        fields: [
            { key: "primary", label: "Primary color" },
        ],
    },
    {
        label: "Surfaces",
        fields: [
            { key: "background", label: "Page background" },
            { key: "header", label: "Sidebar / Header" },
        ],
    },
    {
        label: "Interactive",
        fields: [{ key: "button", label: "Button" }],
    },
    {
        label: "Text",
        fields: [{ key: "text", label: "Body text" }],
    },
];

function BuilderComponent() {
    const { t } = useTranslation();
    const dispatch = useDispatch();
    const brand = getConfig();
    const logoInputRef = useRef(null);
    // Last-known-persisted snapshot of clientComponents — diffed against the
    // live clientComponents at publish time so only genuinely-edited
    // components are sent (this is what keeps Publish scoped to ONLY
    // changed components — one saveSection call per changed key, not one
    // per registered component every time). Computed directly from the raw
    // sections fetch below, normalized through the SAME mergeEntry/
    // buildDefaultEntry machinery ClientBuilderPanel itself uses (fills in
    // byLayout for every layout id, backfills imagePickerSource, etc.) — so
    // both sides of the diff are always the same shape, without ever
    // comparing raw backend rows against ClientBuilderPanel's normalized
    // propsMap (that mismatch was the original over-publishing bug).
    //
    // This must NOT be captured from ClientBuilderPanel's first onChange
    // call (a prior version of this fix did that): ClientBuilderPanel's own
    // initial state can be seeded from a pending localStorage draft instead
    // of the backend value (its "draft wins on the very first value" rule),
    // so that first onChange could report unpublished draft data as if it
    // were the published baseline — making Publish silently no-op ("No
    // changes to publish") even though the backend was never actually
    // updated (under-publishing, the opposite failure mode, but just as
    // wrong). Computing the baseline independently, straight from the
    // backend fetch, is immune to whatever the draft happens to contain.
    const publishedBaselineRef = useRef({});
    // Guards against an out-of-order response: if the admin opens history
    // for component A, then quickly opens it for component B before A's
    // fetch resolves, A's slower response must not overwrite B's correct,
    // already-displayed data.
    const historyRequestIdRef = useRef(0);

    const [target, setTarget] = useState("Admin");
    // Mirrors selectedClientKey's own pattern, scoped to the "Client
    // dashboard" tab's own single-entry ClientBuilderPanel instance — kept
    // separate (not reusing selectedClientKey) since the two panels are
    // independent instances with independent selection state; conflating
    // them would let switching tabs silently retarget the other tab's
    // History button.
    const [selectedDashboardKey, setSelectedDashboardKey] = useState(null);
    const [adminCfg, setAdminCfg] = useState(() => getBrandDefaults().adminConfig);
    const [clientComponents, setClientComponents] = useState({});
    // Bumped on every successful client-site publish — ClientBuilderPanel
    // watches this to know when to clear its refresh-safe localStorage draft.
    const [clientPublishedAt, setClientPublishedAt] = useState(null);
    // Mirrors ClientBuilderPanel's internally-selected component key, so the
    // header-level History button (Client site tab) can target it — same
    // shared openHistory/historyKey/historyData state Admin's History button uses.
    const [selectedClientKey, setSelectedClientKey] = useState(null);
    const [isLoading, setIsLoading] = useState(true);
    const [isSaving, setIsSaving] = useState(false);
    const [logoUploading, setLogoUploading] = useState(false);

    // History drawer state
    const [historyOpen, setHistoryOpen] = useState(false);
    const [historyKey, setHistoryKey] = useState(null);
    const [historyData, setHistoryData] = useState([]);
    const [historyLoading, setHistoryLoading] = useState(false);
    const [historyClearing, setHistoryClearing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const [editingValue, setEditingValue] = useState("");

    // Load admin config + sections on mount
    useEffect(() => {
        Promise.all([
            siteConfigService.getAdminConfig(),
            siteConfigService.getAllSections(),
        ])
            .then(([adminConfig, sections]) => {
                if (adminConfig) {
                    // Drop `secondary`/`footer` from any pre-existing saved
                    // colors blob — both were removed from COLOR_GROUPS/
                    // BRAND_ADMIN_COLORS (confirmed dead: saved + applied as
                    // CSS vars, but zero stylesheet ever consumed them), so a
                    // stale DB row from before this change would otherwise
                    // keep re-publishing them forever via the spread below.
                    const { secondary, footer, ...restColors } = adminConfig.colors || {};
                    const cleanedAdminConfig = { ...adminConfig, colors: restColors };
                    setAdminCfg(prev => ({ ...prev, ...cleanedAdminConfig }));
                    dispatch(setBuilderConfig({ adminConfig: cleanedAdminConfig }));
                }
                if (sections?.length) {
                    const components = Object.fromEntries(sections.map(s => [s.key, s.data]));
                    setClientComponents(components);
                    // Baseline = this raw fetch, normalized the same way
                    // ClientBuilderPanel normalizes its own propsMap — see
                    // publishedBaselineRef's comment above for why this can't
                    // be captured from ClientBuilderPanel's onChange instead.
                    publishedBaselineRef.current = Object.fromEntries(
                        Object.values(COMPONENT_REGISTRY).map((entry) =>
                            [entry.key, mergeEntry(entry, buildDefaultEntry(entry), components[entry.key])]
                        )
                    );
                } else {
                    publishedBaselineRef.current = Object.fromEntries(
                        Object.values(COMPONENT_REGISTRY).map((entry) => [entry.key, buildDefaultEntry(entry)])
                    );
                }
            })
            .catch(() => {})
            .finally(() => setIsLoading(false));
    }, []);

    // Live-apply color changes to the current page as the user picks
    useEffect(() => {
        if (!isLoading) applyAdminTheme(adminCfg.colors);
    }, [adminCfg.colors, isLoading]);

    // Live-apply logo change to Redux (updates sidebar immediately)
    useEffect(() => {
        if (!isLoading) dispatch(setBuilderConfig({ adminConfig: adminCfg }));
    }, [adminCfg.logo, isLoading]);

    const { colors } = adminCfg;

    const setColor = (key, val) => {
        const hex = typeof val === "string" ? val : val.toHexString();
        setAdminCfg(p => ({ ...p, colors: { ...p.colors, [key]: hex } }));
    };

    const handleReset = () => {
        setAdminCfg(getBrandDefaults().adminConfig);
    };

    const handleLogoUpload = (e) => {
        const file = e.target.files[0];
        if (!file) return;
        e.target.value = "";
        setLogoUploading(true);
        siteConfigService.uploadImage(file)
            .then(({ url }) => setAdminCfg(p => ({ ...p, logo: url })))
            .catch(err => toast.error(err.message || "Failed to upload logo"))
            .finally(() => setLogoUploading(false));
    };

    const handlePublish = () => {
        setIsSaving(true);
        if (target === "Admin") {
            siteConfigService.saveAdminConfig(adminCfg)
                .then((data) => {
                    toast.success("Admin config published");
                    dispatch(setBuilderConfig({ adminConfig: data.adminConfig }));
                })
                .catch(err => toast.error(err.message || "Failed to publish"))
                .finally(() => setIsSaving(false));
        } else {
            // Only publish components actually changed since the last
            // successful publish/load — editing just Hero and publishing
            // must not also re-save Navbar/OurAdvantage/FAQ/etc. untouched.
            const baseline = publishedBaselineRef.current;
            const changedEntries = Object.entries(clientComponents).filter(
                ([key, data]) => JSON.stringify(data) !== JSON.stringify(baseline[key])
            );
            if (changedEntries.length === 0) {
                toast.info("No changes to publish");
                setIsSaving(false);
                return;
            }
            const saves = changedEntries.map(([key, data]) =>
                siteConfigService.saveSection(key, data)
            );
            Promise.all(saves)
                .then(() => {
                    toast.success("Changes published");
                    publishedBaselineRef.current = clientComponents;
                    setClientPublishedAt(Date.now());
                })
                .catch(err => toast.error(err.message || "Failed to publish"))
                .finally(() => setIsSaving(false));
        }
    };

    // Open history drawer for any key
    const openHistory = (key) => {
        setHistoryKey(key);
        setHistoryOpen(true);
        setHistoryData([]);
        setHistoryLoading(true);
        // A rename in progress for a PREVIOUS component's history row must
        // not carry over — otherwise the rename input can appear
        // pre-opened for an unrelated row in the newly-opened list.
        setEditingId(null);
        setEditingValue("");
        const requestId = ++historyRequestIdRef.current;
        siteConfigService.getHistory(key)
            .then(rows => {
                if (requestId !== historyRequestIdRef.current) return;
                setHistoryData(rows);
            })
            .catch(() => {
                if (requestId !== historyRequestIdRef.current) return;
                setHistoryData([]);
            })
            .finally(() => {
                if (requestId !== historyRequestIdRef.current) return;
                setHistoryLoading(false);
            });
    };

    const handleDeleteHistory = (id) => {
        siteConfigService.deleteHistory(id)
            .then(() => setHistoryData(prev => prev.filter(r => r.id !== id)))
            .catch(err => toast.error(err.message || "Failed to delete"));
    };

    const handleClearHistory = () => {
        setHistoryClearing(true);
        siteConfigService.clearHistory(historyKey)
            .then(() => {
                setHistoryData([]);
                toast.success("History cleared");
            })
            .catch(err => toast.error(err.message || "Failed to clear history"))
            .finally(() => setHistoryClearing(false));
    };

    const handleRenameHistory = (id, name) => {
        siteConfigService.updateHistoryName(id, name)
            .then(updated => {
                setHistoryData(prev => prev.map(r => r.id === id ? { ...r, name: updated.name } : r));
                setEditingId(null);
            })
            .catch(err => toast.error(err.message || "Failed to rename"));
    };

    const startEditing = (row) => {
        setEditingId(row.id);
        setEditingValue(row.name || "");
    };

    const cancelEditing = () => {
        setEditingId(null);
        setEditingValue("");
    };

    // Auto-numbered version label (v1 = oldest) computed per component key —
    // `historyData` is fetched newest-first (savedAt desc), so version number
    // is derived from ascending `id` order (an auto-increment column), not
    // list position. Display-only: no new DB column, no change to any
    // service call. Recomputed whenever historyData changes.
    const versionedHistoryData = [...historyData]
        .sort((a, b) => a.id - b.id)
        .map((row, i) => ({ ...row, version: i + 1 }))
        .sort((a, b) => b.id - a.id);

    // Load a snapshot into the local editor — user must click Publish to save
    const handleRestoreSnapshot = (historyId) => {
        const row = historyData.find(r => r.id === historyId);
        if (!row) return;
        if (historyKey === ADMIN_KEY) {
            setAdminCfg(prev => ({ ...prev, ...row.data }));
            applyAdminTheme(row.data.colors);
        } else {
            setClientComponents(prev => ({ ...prev, [historyKey]: row.data }));
        }
        toast.info("Snapshot loaded — click Publish changes to save");
        setHistoryOpen(false);
    };

    return (
        <div className={css.root}>
            {/* Header bar */}
            <div className={css.header}>
                <div className={css.headerLeft}>
                    <span className={css.headerTitle}>Builder</span>
                    <Tag color="geekblue" style={{ marginLeft: 6 }}>{brand.title}</Tag>
                </div>

                <Segmented
                    value={target}
                    onChange={setTarget}
                    options={["Admin", "Client site", "Client dashboard"]}
                    size="middle"
                />

                <div className={css.headerRight}>
                    {target === "Admin" && (
                        <>
                            <div className={css.colorDots}>
                                {["primary", "header", "button"].map((k) => (
                                    <Tooltip key={k} title={k}>
                                        <div className={css.dot} style={{ background: colors[k] }} />
                                    </Tooltip>
                                ))}
                            </div>
                            <Button
                                size="small"
                                icon={<HistoryOutlined />}
                                onClick={() => openHistory(ADMIN_KEY)}
                                disabled={isLoading}
                            >
                                History
                            </Button>
                            <Button size="small" onClick={handleReset} disabled={isLoading}>
                                Reset to defaults
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={isSaving}
                                disabled={isLoading}
                                onClick={handlePublish}
                            >
                                Publish changes
                            </Button>
                        </>
                    )}
                    {target === "Client site" && (
                        <>
                            <Button
                                size="small"
                                icon={<HistoryOutlined />}
                                onClick={() => openHistory(selectedClientKey)}
                                disabled={isLoading || !selectedClientKey}
                            >
                                History
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={isSaving}
                                disabled={isLoading}
                                onClick={handlePublish}
                            >
                                Publish changes
                            </Button>
                        </>
                    )}
                    {target === "Client dashboard" && (
                        <>
                            <Button
                                size="small"
                                icon={<HistoryOutlined />}
                                onClick={() => openHistory(selectedDashboardKey)}
                                disabled={isLoading || !selectedDashboardKey}
                            >
                                History
                            </Button>
                            <Button
                                type="primary"
                                icon={<SaveOutlined />}
                                loading={isSaving}
                                disabled={isLoading}
                                onClick={handlePublish}
                            >
                                Publish changes
                            </Button>
                        </>
                    )}
                </div>
            </div>

            {/* Client site builder */}
            {target === "Client site" && (
                <ClientBuilderPanel
                    value={clientComponents}
                    onChange={setClientComponents}
                    publishedAt={clientPublishedAt}
                    onSelectedKeyChange={setSelectedClientKey}
                />
            )}

            {/* Client dashboard builder — same ClientBuilderPanel, scoped to
                DASHBOARD_ONLY_KEYS (dashboardTheme, dashboardPagesTheme) via
                filterKeys — the same shared constant ClientBuilderPanel's own
                "Client site" picker uses to exclude these same keys, so the
                two lists can't drift apart. Shares clientComponents/
                setClientComponents/clientPublishedAt with the "Client site"
                tab (both read/write the same underlying sections state and
                the same generic handlePublish diff path) — only the picker's
                visible entries and this tab's own selectedDashboardKey/
                History targeting are separate. */}
            {target === "Client dashboard" && (
                <ClientBuilderPanel
                    value={clientComponents}
                    onChange={setClientComponents}
                    publishedAt={clientPublishedAt}
                    onSelectedKeyChange={setSelectedDashboardKey}
                    filterKeys={DASHBOARD_ONLY_KEYS}
                />
            )}

            {/* Admin color/logo editor */}
            <div className={css.body} style={{ display: target === "Admin" ? "flex" : "none" }}>
                <div className={css.editorPanel}>
                    <div className={css.editorScroll}>
                        {/* Logo */}
                        <div className={css.colorGroup}>
                            <div className={css.groupLabel}>Logo</div>
                            <div className={css.logoRow}>
                                <img src={adminCfg.logo || brand.logo} alt="Brand logo" className={css.logoThumb} />
                                <div className={css.logoActions}>
                                    <Button size="small" loading={logoUploading} onClick={() => logoInputRef.current?.click()}>Upload</Button>
                                    {adminCfg.logo && (
                                        <Button size="small" danger disabled={logoUploading} onClick={() => setAdminCfg(p => ({ ...p, logo: null }))}>
                                            Remove
                                        </Button>
                                    )}
                                </div>
                            </div>
                            <input
                                ref={logoInputRef}
                                type="file"
                                accept="image/*"
                                style={{ display: "none" }}
                                onChange={handleLogoUpload}
                            />
                        </div>

                        {/* Color groups */}
                        {COLOR_GROUPS.map((g) => (
                            <div key={g.label} className={css.colorGroup}>
                                <div className={css.groupLabel}>{g.label}</div>
                                {g.fields.map(({ key, label }) => (
                                    <div key={key} className={css.colorRow}>
                                        <div className={css.colorInfo}>
                                            <div className={css.swatch} style={{ background: colors[key] }} />
                                            <span className={css.colorLabel}>{label}</span>
                                            <code className={css.colorHex}>{colors[key]}</code>
                                        </div>
                                        <ColorPicker
                                            value={colors[key]}
                                            onChange={(v) => setColor(key, v)}
                                            size="small"
                                        />
                                    </div>
                                ))}
                            </div>
                        ))}
                    </div>
                </div>
            </div>

            {/* History Drawer */}
            <Drawer
                title={`Change history — ${historyKey === ADMIN_KEY ? "Admin config" : historyKey}`}
                open={historyOpen}
                onClose={() => setHistoryOpen(false)}
                width={480}
                extra={
                    <Popconfirm
                        title="Clear all history?"
                        description="This permanently deletes every saved version for this key. This cannot be undone."
                        onConfirm={handleClearHistory}
                        okText="Clear all"
                        okButtonProps={{ danger: true, loading: historyClearing }}
                        cancelText="Cancel"
                        disabled={historyLoading || !historyData.length}
                    >
                        <Button
                            size="small"
                            danger
                            icon={<DeleteOutlined />}
                            disabled={historyLoading || historyClearing || !historyData.length}
                        >
                            Clear all
                        </Button>
                    </Popconfirm>
                }
            >
                <List
                    loading={historyLoading}
                    dataSource={versionedHistoryData}
                    locale={{
                        emptyText: (
                            <span style={{ color: "#595959", fontSize: 13 }}>
                                No history yet — changes are recorded after the first publish.
                            </span>
                        )
                    }}
                    renderItem={(row, index) => {
                        const { actions, title, description } = HistoryRow({
                            row,
                            index,
                            isEditing: editingId === row.id,
                            editingValue,
                            onEditingValueChange: setEditingValue,
                            onStartEdit: startEditing,
                            onCancelEdit: cancelEditing,
                            onRename: handleRenameHistory,
                            onRestore: handleRestoreSnapshot,
                            onDelete: handleDeleteHistory,
                        });
                        return (
                            <List.Item actions={actions}>
                                <List.Item.Meta title={title} description={description} />
                            </List.Item>
                        );
                    }}
                />
            </Drawer>
        </div>
    );
}

export { BuilderComponent };
